import React, { Children, useEffect, useRef, useState } from 'react';
import {
    Checkbox,
    CustomChipInputField,
    DatePicker,
    Drawer,
    Select,
    TextField,
} from '@biq/apex-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, MenuItem, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useDictionaryReference } from '../../../../hooks/useDictionaryReference';
import {
    checkForCaseSensitiveDuplicates,
    glossaryMetadataFormStyles,
    possiblySetDuplicateErrors,
} from '../../../glossary-metadata/glossary-metadata-helper';
import { useIndexList } from '../../contexts/IndexListContext';
import { REFERENCE_TYPE } from '../../dicitionary.helper';
import {
    attributeSchema,
    DATA_TYPE_GROUPS,
    DATA_TYPE_VALUES,
    getDTGroup,
} from '../../dictionary-manager/dictionary-manager-helper';
import { useAttributeList } from '../../contexts/AttributeListContext';
import { MultilineTextField } from 'components';
import {
    Attribute,
    IDataType,
    getAvailableAttributeList,
    validationsMessages,
    Dictionary,
    globalEllipsisTextStyles,
} from 'lib';
import { AttributesComponent } from '../drawers/AttributesComponent';
import { EditDrawerCancelModal } from '../drawers/EditDrawerCancelModal';
import {
    attributeDrawerStyles,
    AttributeIcons,
    AttributeRecord,
    getModifiedAttributeIconRecords,
    getModifiedAttributeKey,
    getUTCFormattedDateString,
    getNormalizedDisplayAttributeKey,
    PopoverWithArrow,
    getSortedAttributeTypes,
} from './attribute-helper';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { DataTypeWarning } from './DataTypeWarning';
import { EditDrawerFooterWithError } from './EditDrawerFooterWithError';
dayjs.extend(utc);

interface DictionaryAttributeEditDrawerProps {
    setIsChangesUnsaved?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const DictionaryAttributeEditDrawer = ({
    setIsChangesUnsaved,
}: DictionaryAttributeEditDrawerProps): React.JSX.Element => {
    const {
        selectedAttribute: attribute,
        setSelectedAttribute,
        setAttribute,
        deleteAttribute,
    } = useAttributeList();
    const scrollableDivRef = useRef<HTMLDivElement>(null);
    const [enumValues, setEnumValues] = useState<string[]>([]);
    const defaultVals = { ...attribute };
    const [dataTypeGroup, setDataTypeGroup] = useState<string>(
        DATA_TYPE_GROUPS.STRING
    );
    const [openCancelModal, setOpenCancelModal] = useState(false);
    const [isValuesChanged, setIsValuesChanged] = useState<boolean>(false);
    const [displayDate, setDisplayDate] = useState<any>(null);
    const [displayDateTime, setDisplayDateTime] = useState<any>(null);
    const [renameOrDeleteError, setRenameOrDeleteError] =
        useState<boolean>(false);
    const [dataTypeChanged, setDataTypeChanged] = useState<boolean>(false);
    const [displayWarningModal, setDisplayWarningModal] =
        useState<boolean>(false);

    const { data: referenceData } = useDictionaryReference({
        type: dataTypeGroup,
    });
    const { attributes } = useAttributeList();
    const { indexes } = useIndexList();
    const attributesData = getAvailableAttributeList(attributes);

    const tooltipRef = useRef(null);

    const {
        watch,
        getValues,
        control,
        setValue,
        clearErrors,
        setError,
        formState: { errors },
        reset,
    } = useForm<Attribute>({
        mode: 'all',
        defaultValues: defaultVals,
        resolver: yupResolver(attributeSchema),
    });

    useEffect(() => {
        if (attribute?.enumValues) {
            setEnumValues(attribute?.enumValues ?? []);
        }
        reset(attribute);
    }, [attribute, reset]);

    useEffect(() => {
        possiblySetDuplicateErrors(
            'enumValues',
            setError,
            clearErrors,
            enumValues
        );
    }, [enumValues]); // eslint-disable-line react-hooks/exhaustive-deps

    const [
        attributeName,
        attributeDataType,
        fingerprintAttributes,
        referenceDictionaryName,
    ] = watch([
        'name',
        'datatype',
        'fingerprintAttributes',
        'referenceDictionaryName',
    ]);

    const resetRenameOrDeleteError = (): void => {
        setRenameOrDeleteError(false);
    };

    useEffect(() => {
        resetRenameOrDeleteError();
    }, [fingerprintAttributes]);

    useEffect(() => {
        const selectedDataType = getValues()?.datatype ?? '';
        const group = getDTGroup(selectedDataType);
        setDataTypeGroup(group ?? DATA_TYPE_GROUPS.STRING);

        if (dataTypeGroup === DATA_TYPE_GROUPS.DATE) {
            setDisplayDate(
                attribute?.defaultValue ? dayjs(attribute.defaultValue) : null
            );
        }
        if (dataTypeGroup === DATA_TYPE_GROUPS.DATETIME) {
            setDisplayDateTime(
                attribute?.defaultValue ? dayjs(attribute.defaultValue) : null
            );
        }

        if (scrollableDivRef.current) {
            scrollableDivRef.current.scrollTop =
                scrollableDivRef.current.scrollHeight;
        }
    }, [
        attributeDataType,
        attribute,
        renameOrDeleteError,
        getValues,
        dataTypeGroup,
    ]);

    const isCurrentAttributeReferenceType = (type: string): boolean => {
        return type.toLowerCase().includes(REFERENCE_TYPE);
    };

    const handleCancelModal = (): void => {
        setOpenCancelModal(false);
    };

    const removeWarningModal = (): void => {
        setDisplayWarningModal(false);
    };

    const handleClose = (): void => {
        handleCancelModal();
        setSelectedAttribute();
        setIsValuesChanged(false);
        setRenameOrDeleteError(false);
        setDataTypeChanged(false);
        removeWarningModal();
    };

    const handleOnClose = (): void => {
        if (isValuesChanged || dataTypeChanged) {
            setOpenCancelModal(true);
        } else {
            handleClose();
        }
    };

    const isCurrentAttributePartOfAnIndex = (): boolean => {
        const currentAttribute = attribute.name ?? '';
        return indexes.some((index) =>
            (index.attributes ?? []).find(
                (attrib) => attrib === currentAttribute
            )
        );
    };

    const isAttributeRenamed = (): boolean => {
        return attributeName !== attribute.name;
    };

    const isFormValid = (): boolean => {
        const isNameSelected = !!attributeName;
        const isDatatypeSelected = !!attributeDataType;
        const areEnumValuesDuplicated =
            checkForCaseSensitiveDuplicates(enumValues);
        const canRenameOrDeleteAttribute = isAttributeRenamed()
            ? !isCurrentAttributePartOfAnIndex()
            : true;
        const isReferenceDictionaryNameSelected =
            isCurrentAttributeReferenceType(attributeDataType ?? '')
                ? !!referenceDictionaryName &&
                  referenceDictionaryName !== 'None'
                : true;

        if (!isNameSelected) {
            setError('name', { message: validationsMessages.required });
        }
        if (!isDatatypeSelected) {
            setError('datatype', { message: validationsMessages.required });
        }
        if (areEnumValuesDuplicated) {
            setError('enumValues', {
                message: validationsMessages.duplicatedOptions,
            });
        }
        if (!canRenameOrDeleteAttribute) {
            setRenameOrDeleteError(true);
        }
        if (!isReferenceDictionaryNameSelected) {
            setError('referenceDictionaryName', {
                message: validationsMessages.required,
            });
        }

        return (
            isNameSelected &&
            isDatatypeSelected &&
            isReferenceDictionaryNameSelected &&
            !areEnumValuesDuplicated &&
            canRenameOrDeleteAttribute
        );
    };

    const possiblySaveForm = (): void => {
        if (dataTypeChanged) {
            setIsChangesUnsaved?.(true);
        }
        if (isFormValid()) {
            const attrib = getValues();

            if (dataTypeGroup === DATA_TYPE_GROUPS.ENUM) {
                attrib.enumValues = enumValues;
            }
            if (
                dataTypeGroup === DATA_TYPE_GROUPS.DATE &&
                displayDate !== null
            ) {
                attrib.defaultValue = getUTCFormattedDateString(displayDate);
            }
            if (
                dataTypeGroup === DATA_TYPE_GROUPS.DATETIME &&
                displayDateTime !== null
            ) {
                attrib.defaultValue =
                    getUTCFormattedDateString(displayDateTime);
            }

            // if the current attribute is of DataTypeGroup object, set the error flag
            // to apply the error state styles but also mark the attribute list as invalid.
            if (dataTypeGroup === DATA_TYPE_GROUPS.OBJECT) {
                attrib.error = true;
            }

            if (isAttributeRenamed()) {
                setIsChangesUnsaved?.(true);
            }

            setAttribute(attribute.id, attrib);
            setSelectedAttribute(undefined);
            setIsValuesChanged(false);
            resetRenameOrDeleteError();
            removeWarningModal();
            setDataTypeChanged(false);
        }
    };

    const onSubmit = (): void => {
        // if it's a brand new attribute then don't display the warning modal on data type changed.
        if (
            dataTypeChanged &&
            attribute.treeViewDisplayName === 'newAttribute'
        ) {
            possiblySaveForm();
        }
        // if it's a previous saved attribute, only display the warning modal if the form is valid so the user can make changes.
        else if (
            dataTypeChanged &&
            attribute.treeViewDisplayName !== 'newAttribute' &&
            isFormValid()
        ) {
            setDisplayWarningModal(true);
        } else {
            possiblySaveForm();
        }
    };

    const onDeleteAttribute = (): void => {
        if (isCurrentAttributePartOfAnIndex()) {
            setRenameOrDeleteError(true);
        } else {
            deleteAttribute(attribute);
            setIsChangesUnsaved?.(true);
            handleClose();
        }
    };

    const findAttributeInRecord = (
        record: Record<string, any>,
        attributeKey: string
    ): AttributeRecord | undefined => {
        for (const [key, value] of Object.entries(record)) {
            if (key === attributeKey) {
                return { key, value };
            }
        }
        return undefined;
    };

    const getAttribute = (att: IDataType): React.JSX.Element | null => {
        const modifiedAttributeIcons =
            getModifiedAttributeIconRecords(AttributeIcons);
        const attributeRecord: AttributeRecord | undefined =
            findAttributeInRecord(
                modifiedAttributeIcons,
                getModifiedAttributeKey(att.label)
            );

        if (attributeRecord) {
            return (
                <Grid sx={attributeDrawerStyles.attributeItemContainer}>
                    <img
                        src={attributeRecord.value}
                        alt={attributeRecord.key}
                        style={attributeDrawerStyles.attributeItemImg}
                    />
                    <Typography sx={attributeDrawerStyles.attributeItemText}>
                        {getNormalizedDisplayAttributeKey(attributeRecord.key)}
                    </Typography>
                </Grid>
            );
        }
        return null;
    };

    // Display the default value field for specific selected data types rather than the dataTypeGroup.
    const displayDefaultValueField = (): boolean => {
        return (
            attributeDataType === DATA_TYPE_VALUES.STRING ||
            attributeDataType === DATA_TYPE_VALUES.ENUM ||
            attributeDataType === DATA_TYPE_VALUES.BOOLEAN ||
            attributeDataType === DATA_TYPE_VALUES.INTEGER ||
            attributeDataType === DATA_TYPE_VALUES.LONG ||
            attributeDataType === DATA_TYPE_VALUES.DECIMAL ||
            isCurrentAttributeReferenceType(attributeDataType ?? '')
        );
    };

    const displayAttributesField = (): boolean => {
        return (
            dataTypeGroup === DATA_TYPE_GROUPS.FINGERPRINT ||
            dataTypeGroup === DATA_TYPE_GROUPS.VECTOR
        );
    };

    const displayMinMaxFields = (): boolean => {
        return (
            dataTypeGroup === DATA_TYPE_GROUPS.NUMBER ||
            dataTypeGroup === DATA_TYPE_GROUPS.DECIMAL
        );
    };

    const displayRequiredField = (): boolean => {
        return dataTypeGroup !== DATA_TYPE_GROUPS.BLOB;
    };

    const displayDictionaryReferenceField = (): boolean => {
        return dataTypeGroup.includes(REFERENCE_TYPE);
    };

    const updateUnsavedChanges = (): void => {
        setIsValuesChanged(true);
        setIsChangesUnsaved?.(true);
    };

    const handleDataTypeChanged = (): void => {
        // when user changes the data type, we want to reset the current values for special fields.
        setValue('fingerprintAttributes', []);
        setValue('defaultValue', '');
        setValue('regex', '');
        setValue('min', undefined);
        setValue('max', undefined);
        setValue('precision', 2);
        setValue('referenceDictionaryName', '');
        setDisplayDate(null);
        setDisplayDateTime(null);
        setEnumValues([]);
        clearErrors('min');
        clearErrors('max');
        clearErrors('precision');
        clearErrors('referenceDictionaryName');

        // only set data type changed for already saved attributes. If this is a new one, we don't need to display the warning before it's saved.
        if (attribute?.datatype) {
            setDataTypeChanged(true);
        }
    };

    const handleDateChange = (date: Date | null): void => {
        // set the selected date at midnight.
        setDisplayDate(dayjs(date).startOf('day'));
    };

    const handleDateTimeChange = (date: Date | null): void => {
        setDisplayDateTime(dayjs(date));
    };

    const handleEnumChange = (value: string[]): void => {
        setEnumValues(value);
        resetRenameOrDeleteError();
    };

    const resetDataTypeUniqueValues = (): void => {
        // reset original attribute data type
        setValue('datatype', attribute.datatype);

        // possibly reset default value
        if (attribute.defaultValue) {
            setValue('defaultValue', attribute.defaultValue);
        }

        // reset all the unique values associated with the original attribute data type.
        if (isCurrentAttributeReferenceType(attribute.datatype)) {
            setValue(
                'referenceDictionaryName',
                attribute.referenceDictionaryName
            );
        }
        if (getDTGroup(attribute.datatype) === DATA_TYPE_GROUPS.STRING) {
            setValue('regex', attribute.regex);
        }
        if (getDTGroup(attribute.datatype) === DATA_TYPE_GROUPS.DECIMAL) {
            setValue('precision', attribute.precision);
            setValue('min', attribute.min);
            setValue('max', attribute.max);
        }
        if (getDTGroup(attribute.datatype) === DATA_TYPE_GROUPS.NUMBER) {
            setValue('min', attribute.min);
            setValue('max', attribute.max);
        }
        if (getDTGroup(attribute.datatype) === DATA_TYPE_GROUPS.ENUM) {
            setValue('enumValues', attribute.enumValues);
            handleEnumChange(attribute.enumValues);
        }
        if (getDTGroup(attribute.datatype) === DATA_TYPE_GROUPS.FINGERPRINT) {
            setValue('fingerprintAttributes', attribute.fingerprintAttributes);
        }
    };

    const onWarningModalCancel = (): void => {
        removeWarningModal();
        setDataTypeChanged(false);
        resetDataTypeUniqueValues();
    };

    const onWarningModalConfirm = (): void => {
        possiblySaveForm();
    };

    return (
        <Drawer
            closable
            viewMode='edit'
            open={!!attribute}
            drawerType={'rounded'}
            anchor={'right'}
            sx={attributeDrawerStyles.drawer}
            onClose={handleOnClose}
            title={'Edit Attribute'}
            titleStyles={attributeDrawerStyles.title}
            footerChildren={
                <div ref={tooltipRef}>
                    <EditDrawerFooterWithError
                        displayFooterError={renameOrDeleteError}
                        containerStyles={attributeDrawerStyles.footerContainer}
                        onDeleteAttribute={onDeleteAttribute}
                        onSubmit={onSubmit}
                    />
                    <PopoverWithArrow
                        open={displayWarningModal}
                        anchorEl={tooltipRef.current}
                    >
                        <DataTypeWarning
                            isObjectDataType={
                                getDTGroup(attribute?.datatype ?? '') ===
                                DATA_TYPE_GROUPS.OBJECT
                            }
                            handleCancel={onWarningModalCancel}
                            handleConfirm={onWarningModalConfirm}
                        />
                    </PopoverWithArrow>
                </div>
            }
        >
            <Grid
                sx={
                    renameOrDeleteError
                        ? {
                              ...attributeDrawerStyles.bodyContainer,
                              ...attributeDrawerStyles.overFlowBodyContainer,
                          }
                        : attributeDrawerStyles.bodyContainer
                }
                ref={scrollableDivRef}
            >
                <Grid sx={attributeDrawerStyles.itemContainer}>
                    <Controller
                        name='name'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                required
                                variant='outlined'
                                label='Name'
                                onChange={(value): void => {
                                    resetRenameOrDeleteError();
                                    field.onChange(value);
                                }}
                                sx={attributeDrawerStyles.textFields}
                                error={!!errors?.name}
                                errorText={
                                    errors.name ? errors?.name?.message : ''
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid sx={attributeDrawerStyles.itemContainer}>
                    <MultilineTextField
                        control={control}
                        label='Description'
                        name='description'
                        onChange={(): void => {
                            updateUnsavedChanges();
                            resetRenameOrDeleteError();
                        }}
                    />
                </Grid>
                <Grid sx={attributeDrawerStyles.itemContainer}>
                    <Controller
                        name='displayName'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                variant='outlined'
                                label='Display Name'
                                sx={attributeDrawerStyles.textFields}
                                onChange={(value): void => {
                                    updateUnsavedChanges();
                                    resetRenameOrDeleteError();
                                    field.onChange(value);
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid sx={attributeDrawerStyles.itemContainer}>
                    <Controller
                        name='datatype'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <Select
                                {...field}
                                required
                                label='Data Type'
                                variant='outlined'
                                error={!!errors.datatype}
                                errorText={
                                    errors.datatype
                                        ? errors.datatype.message
                                        : ''
                                }
                                MenuProps={{
                                    sx: attributeDrawerStyles.dropDownMenuHeight,
                                }}
                                onChange={(value): void => {
                                    field.onChange(value);
                                    handleDataTypeChanged();
                                    resetRenameOrDeleteError();
                                }}
                            >
                                {Children.toArray(
                                    getSortedAttributeTypes().map((type) => (
                                        <MenuItem value={type.value}>
                                            {getAttribute(type)}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        )}
                    />
                </Grid>
                {displayRequiredField() && (
                    <Grid
                        sx={
                            dataTypeGroup === DATA_TYPE_GROUPS.FINGERPRINT
                                ? attributeDrawerStyles.requiredItemContainer
                                : attributeDrawerStyles.itemContainer
                        }
                    >
                        <Controller
                            name='required'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <Checkbox
                                    {...field}
                                    defaultChecked={!!attribute?.required}
                                    label={'Required'}
                                    onChange={(value): void => {
                                        updateUnsavedChanges();
                                        field.onChange(value);
                                        resetRenameOrDeleteError();
                                    }}
                                />
                            )}
                        />
                    </Grid>
                )}
                {attributeDataType === DATA_TYPE_VALUES.DATE && (
                    <Grid sx={attributeDrawerStyles.itemContainer}>
                        <DatePicker
                            variant='DesktopDatePicker'
                            label='Default Value'
                            size='medium'
                            value={displayDate}
                            onChange={(value: Date | null): void => {
                                updateUnsavedChanges();
                                handleDateChange(value);
                                resetRenameOrDeleteError();
                            }}
                            error={false}
                            helperText={''}
                            disablePast={false}
                        />
                    </Grid>
                )}
                {attributeDataType === DATA_TYPE_VALUES.DATETIME && (
                    <Grid sx={attributeDrawerStyles.itemContainer}>
                        <DatePicker
                            variant='DesktopDateTimePicker'
                            label='Default Value'
                            size='medium'
                            value={displayDateTime}
                            onChange={(value: Date | null): void => {
                                updateUnsavedChanges();
                                handleDateTimeChange(value);
                                resetRenameOrDeleteError();
                            }}
                            error={false}
                            helperText={''}
                            disablePast={false}
                        />
                    </Grid>
                )}
                {dataTypeGroup === DATA_TYPE_GROUPS.STRING && (
                    <Grid sx={attributeDrawerStyles.itemContainer}>
                        <Controller
                            name='regex'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <TextField
                                    {...field}
                                    variant='outlined'
                                    label='Regex'
                                    sx={attributeDrawerStyles.textFields}
                                    onChange={(value): void => {
                                        updateUnsavedChanges();
                                        resetRenameOrDeleteError();
                                        field.onChange(value);
                                    }}
                                />
                            )}
                        />
                    </Grid>
                )}
                {dataTypeGroup === DATA_TYPE_GROUPS.ENUM && (
                    <Grid sx={attributeDrawerStyles.itemContainer}>
                        <Controller
                            name='enumValues'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <CustomChipInputField
                                    {...field}
                                    required={false}
                                    fieldValue={enumValues}
                                    setFieldValue={handleEnumChange}
                                    size='medium'
                                    label='Enumerations'
                                    helperText='Enter one or more values separated by a tab.'
                                    error={!!errors?.enumValues}
                                    errorText={
                                        errors?.enumValues?.message ?? ''
                                    }
                                    customStyles={
                                        glossaryMetadataFormStyles.customChipInputStyles
                                    }
                                    allowEmbeddedCommas
                                />
                            )}
                        />
                    </Grid>
                )}

                {displayMinMaxFields() && (
                    <>
                        <Grid sx={attributeDrawerStyles.itemContainer}>
                            <Controller
                                name='min'
                                control={control}
                                render={({ field }): React.JSX.Element => (
                                    <TextField
                                        {...field}
                                        variant='outlined'
                                        label='Min'
                                        helperText={
                                            dataTypeGroup ===
                                            DATA_TYPE_GROUPS.NUMBER
                                                ? 'Numerical values only'
                                                : 'Numerical and decimal values only'
                                        }
                                        sx={attributeDrawerStyles.textFields}
                                        onChange={(value): void => {
                                            updateUnsavedChanges();
                                            resetRenameOrDeleteError();
                                            field.onChange(value);
                                        }}
                                        value={`${getValues()?.min ?? ''}`}
                                        error={!!errors?.min}
                                        errorText={errors?.min?.message ?? ''}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid sx={attributeDrawerStyles.itemContainer}>
                            <Controller
                                name='max'
                                control={control}
                                render={({ field }): React.JSX.Element => (
                                    <TextField
                                        {...field}
                                        variant='outlined'
                                        label='Max'
                                        helperText={
                                            dataTypeGroup ===
                                            DATA_TYPE_GROUPS.NUMBER
                                                ? 'Numerical values only'
                                                : 'Numerical and decimal values only'
                                        }
                                        sx={attributeDrawerStyles.textFields}
                                        onChange={(value): void => {
                                            updateUnsavedChanges();
                                            resetRenameOrDeleteError();
                                            field.onChange(value);
                                        }}
                                        value={`${getValues()?.max ?? ''}`}
                                        error={!!errors?.max}
                                        errorText={errors?.max?.message ?? ''}
                                    />
                                )}
                            />
                        </Grid>
                    </>
                )}
                {dataTypeGroup === DATA_TYPE_GROUPS.DECIMAL && (
                    <Grid sx={attributeDrawerStyles.itemContainer}>
                        <Controller
                            name='precision'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <TextField
                                    {...field}
                                    required
                                    variant='outlined'
                                    label='Precision'
                                    helperText='Must be a value between 1 and 15'
                                    sx={attributeDrawerStyles.textFields}
                                    onChange={(value): void => {
                                        updateUnsavedChanges();
                                        resetRenameOrDeleteError();
                                        field.onChange(value);
                                    }}
                                    value={`${getValues()?.precision ?? ''}`}
                                    error={!!errors?.precision}
                                    errorText={errors?.precision?.message ?? ''}
                                />
                            )}
                        />
                    </Grid>
                )}
                {displayDictionaryReferenceField() && (
                    <Grid sx={attributeDrawerStyles.itemContainer}>
                        <Controller
                            name='referenceDictionaryName'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <Select
                                    {...field}
                                    required
                                    label='Reference Dictionary'
                                    variant='outlined'
                                    error={!!errors.referenceDictionaryName}
                                    errorText={
                                        errors.referenceDictionaryName
                                            ? validationsMessages.required
                                            : ''
                                    }
                                    MenuProps={{
                                        sx: attributeDrawerStyles.dictionaryReferenceDropdown,
                                    }}
                                    onChange={(value): void => {
                                        updateUnsavedChanges();
                                        resetRenameOrDeleteError();
                                        field.onChange(value);
                                    }}
                                >
                                    {(referenceData?.data ?? []).length > 0 ? (
                                        Children.toArray(
                                            (referenceData?.data ?? []).map(
                                                (dictionary: Dictionary) => (
                                                    <MenuItem
                                                        value={dictionary.name}
                                                        sx={
                                                            attributeDrawerStyles.dictionaryReferenceContainer
                                                        }
                                                    >
                                                        <Typography
                                                            sx={
                                                                globalEllipsisTextStyles
                                                            }
                                                        >
                                                            {dictionary.name}
                                                        </Typography>
                                                    </MenuItem>
                                                )
                                            )
                                        )
                                    ) : (
                                        <MenuItem key={'None'} value={'None'}>
                                            None
                                        </MenuItem>
                                    )}
                                </Select>
                            )}
                        />
                    </Grid>
                )}
                {displayDefaultValueField() && (
                    <Grid sx={attributeDrawerStyles.itemContainer}>
                        <Controller
                            name='defaultValue'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <TextField
                                    {...field}
                                    variant='outlined'
                                    label='Default Value'
                                    onChange={(value): void => {
                                        updateUnsavedChanges();
                                        resetRenameOrDeleteError();
                                        field.onChange(value);
                                    }}
                                    sx={attributeDrawerStyles.textFields}
                                    error={!!errors?.defaultValue}
                                    errorText={
                                        errors.defaultValue
                                            ? errors?.defaultValue?.message
                                            : ''
                                    }
                                />
                            )}
                        />
                    </Grid>
                )}
                {displayAttributesField() && (
                    <AttributesComponent
                        attributeKey='fingerprintAttributes'
                        attributesData={attributesData}
                        selectedAttributesData={fingerprintAttributes ?? []}
                        setValue={setValue}
                        setIsValuesChanged={setIsValuesChanged}
                        clearErrors={clearErrors}
                        errors={errors}
                        errorText={errors?.fingerprintAttributes?.message ?? ''}
                        customAttributesSelectedListContainerStyles={
                            attributeDrawerStyles.customAttributesSelectedListContainerStyles
                        }
                        customAttributesMainListContainerStyles={
                            attributeDrawerStyles.customAttributesMainListContainerStyles
                        }
                    />
                )}
                {openCancelModal && (
                    <EditDrawerCancelModal
                        cancelModal={openCancelModal}
                        handleCancelModal={handleCancelModal}
                        handleCancel={handleClose}
                    />
                )}
            </Grid>
        </Drawer>
    );
};
